/**
 * AdminProjectsViewPage - Admin view for all project templates
 * Full access to all fields, download JSON, and generate projects
 * Only accessible to admin users
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  Divider,
  Alert,
  CircularProgress,
  Pagination,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  PlayArrow as GenerateIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

interface ProjectTemplate {
  code: string;
  title: string;
  problem_statement: string;
  main_objective: string;
  target_groups: string[];
  expected_results: string[];
  innovation_elements: string;
  eu_priorities: string[];
}

interface AdminProjectsViewPageProps {
  onBack: () => void;
  onGenerateProject?: (project: ProjectTemplate) => void;
}

const AdminProjectsViewPage: React.FC<AdminProjectsViewPageProps> = ({ onBack, onGenerateProject }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectTemplate | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const projectsPerPage = 20;

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm?.trim()) params.append('search', searchTerm.trim());
      params.append('limit', String(projectsPerPage));
      params.append('offset', String((page - 1) * projectsPerPage));

      const response = await fetch(`${API_URL}/api/projects/showcase?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
        setTotalProjects(data.total || 0);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const downloadProjectJSON = (project: ProjectTemplate) => {
    const jsonString = JSON.stringify(project, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.code}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (project: ProjectTemplate) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(project, null, 2));
      setCopiedCode(project.code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGenerateClick = (project: ProjectTemplate) => {
    setSelectedProject(project);
    setGenerateDialogOpen(true);
  };

  const handleConfirmGenerate = async () => {
    if (!selectedProject || !onGenerateProject) return;
    
    setGenerating(true);
    try {
      onGenerateProject(selectedProject);
      setGenerateDialogOpen(false);
    } catch (error) {
      console.error('Error generating project:', error);
    } finally {
      setGenerating(false);
    }
  };

  const totalPages = Math.ceil(totalProjects / projectsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#1a1a2e' }}>
      {/* Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: '#16213e',
          borderBottom: '2px solid #e94560'
        }}
      >
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ color: 'white' }}
          >
            Back
          </Button>
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1, color: 'white', fontWeight: 700 }}>
            🔐 Admin: All Project Templates
          </Typography>
          <Chip 
            label={`${totalProjects.toLocaleString()} projects`}
            sx={{ bgcolor: '#e94560', color: 'white', fontWeight: 600 }}
          />
        </Toolbar>
      </AppBar>

      {/* Search Bar */}
      <Box sx={{ bgcolor: '#16213e', py: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Container maxWidth="lg">
          <TextField
            fullWidth
            placeholder="Search projects by title, code, or content..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: 'rgba(255,255,255,0.05)',
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e94560'
                }
              }
            }}
          />
        </Container>
      </Box>

      {/* Projects List */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#e94560' }} />
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>Loading projects...</Typography>
          </Box>
        ) : projects.length === 0 ? (
          <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
            No projects found matching your search.
          </Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              {projects.map((project) => (
                <Grid item xs={12} key={project.code}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderColor: '#e94560'
                      }
                    }}
                  >
                    <CardContent>
                      {/* Header Row */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Chip
                              label={project.code}
                              size="small"
                              sx={{
                                bgcolor: '#e94560',
                                color: 'white',
                                fontWeight: 700,
                                fontFamily: 'monospace'
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                              {project.eu_priorities.length} priorities • {project.target_groups.length} target groups
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              lineHeight: 1.3
                            }}
                          >
                            {project.title}
                          </Typography>
                        </Box>
                        
                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                          <Tooltip title="Copy JSON">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(project)}
                              sx={{ 
                                color: copiedCode === project.code ? '#4CAF50' : 'rgba(255,255,255,0.5)',
                                '&:hover': { color: 'white' }
                              }}
                            >
                              {copiedCode === project.code ? <CheckIcon /> : <CopyIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download JSON">
                            <IconButton
                              size="small"
                              onClick={() => downloadProjectJSON(project)}
                              sx={{ 
                                color: 'rgba(255,255,255,0.5)',
                                '&:hover': { color: '#FFCC00' }
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate this project">
                            <IconButton
                              size="small"
                              onClick={() => handleGenerateClick(project)}
                              sx={{ 
                                color: 'rgba(255,255,255,0.5)',
                                '&:hover': { color: '#4CAF50' }
                              }}
                            >
                              <GenerateIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={expandedProject === project.code ? "Collapse" : "View all fields"}>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedProject(
                                expandedProject === project.code ? null : project.code
                              )}
                              sx={{ 
                                color: expandedProject === project.code ? '#e94560' : 'rgba(255,255,255,0.5)',
                                '&:hover': { color: '#e94560' }
                              }}
                            >
                              {expandedProject === project.code ? <ExpandLessIcon /> : <ViewIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Preview */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          lineHeight: 1.6,
                          display: expandedProject === project.code ? 'none' : 'block'
                        }}
                      >
                        {project.main_objective.length > 200
                          ? project.main_objective.substring(0, 200) + '...'
                          : project.main_objective
                        }
                      </Typography>

                      {/* Tags Preview */}
                      {expandedProject !== project.code && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                          {project.eu_priorities.slice(0, 4).map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 204, 0, 0.2)',
                                color: '#FFCC00',
                                fontSize: '0.7rem',
                                height: 22
                              }}
                            />
                          ))}
                          {project.eu_priorities.length > 4 && (
                            <Chip
                              label={`+${project.eu_priorities.length - 4}`}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 22, color: 'rgba(255,255,255,0.5)' }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Expanded Content */}
                      <Collapse in={expandedProject === project.code}>
                        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
                        
                        <Grid container spacing={3}>
                          {/* Problem Statement */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              PROBLEM STATEMENT
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                                {project.problem_statement}
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Main Objective */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              MAIN OBJECTIVE
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                                {project.main_objective}
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Target Groups */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              TARGET GROUPS ({project.target_groups.length})
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {project.target_groups.map((tg, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tg}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(255,255,255,0.1)',
                                      color: 'white'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Paper>
                          </Grid>

                          {/* EU Priorities */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              EU PRIORITIES ({project.eu_priorities.length})
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {project.eu_priorities.map((tag, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tag}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(255, 204, 0, 0.2)',
                                      color: '#FFCC00'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Expected Results */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              EXPECTED RESULTS ({project.expected_results.length})
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {project.expected_results.map((result, idx) => (
                                  <Typography
                                    component="li"
                                    variant="body2"
                                    key={idx}
                                    sx={{ color: 'rgba(255,255,255,0.9)', mb: 1, lineHeight: 1.6 }}
                                  >
                                    {result}
                                  </Typography>
                                ))}
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Innovation Elements */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: '#e94560', fontWeight: 600, mb: 1 }}>
                              INNOVATION ELEMENTS
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                                {project.innovation_elements}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadProjectJSON(project)}
                            sx={{
                              borderColor: '#FFCC00',
                              color: '#FFCC00',
                              '&:hover': { bgcolor: 'rgba(255, 204, 0, 0.1)' }
                            }}
                          >
                            Download JSON
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<GenerateIcon />}
                            onClick={() => handleGenerateClick(project)}
                            sx={{
                              bgcolor: '#4CAF50',
                              '&:hover': { bgcolor: '#388E3C' }
                            }}
                          >
                            Generate Project
                          </Button>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .Mui-selected': {
                      bgcolor: '#e94560 !important',
                      color: 'white !important'
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Generate Project Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#16213e',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Generate Project from Template
          </Typography>
          <IconButton onClick={() => setGenerateDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedProject && (
            <Box>
              <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)' }}>
                This will use the template as a starting point for a new project generation.
              </Alert>
              <Typography variant="subtitle2" sx={{ color: '#e94560', mb: 1 }}>
                Template:
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedProject.title}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#e94560', mb: 1 }}>
                Code:
              </Typography>
              <Chip label={selectedProject.code} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setGenerateDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : <GenerateIcon />}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#388E3C' }
            }}
          >
            {generating ? 'Starting...' : 'Generate Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProjectsViewPage;
